import React, { useEffect, useState } from "react";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/axios";
import Select from "react-select";
import { cleanNumber, formatCOPInput } from "../../utils/format";

const normalizePrice = (value) => {
  if (value === "" || value == null) return "";
  const number = Number(value);
  return Number.isFinite(number) ? String(Math.round(number)) : "";
};

function ReceivePurchaseOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [brands, setBrands] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/purchase-orders/${id}`), api.get("/brands")])
      .then(([orderResponse, brandsResponse]) => {
        const loadedOrder = orderResponse.data;
        setOrder(loadedOrder);
        setBrands(brandsResponse.data || []);
        const initialRows = {};
        loadedOrder.details.forEach((detail) => {
          initialRows[detail.id] = [{
            quantity: Math.max(
              0,
              Number(detail.quantity) - Number(detail.received_quantity || 0),
            ),
            brand_id: "",
            batch_number: "",
            invima_code: "",
            expiration_date: "",
            unit_price: normalizePrice(detail.unit_price),
          }];
        });
        setRows(initialRows);
      })
      .catch(() => toast.error("Error al cargar la orden para recepción"));
  }, [id]);

  const updateRow = (detailId, rowIndex, field, value) => {
    setRows((current) => ({
      ...current,
      [detailId]: current[detailId].map((row, index) =>
        index === rowIndex ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const addLot = (detail) => {
    setRows((current) => ({
      ...current,
      [detail.id]: [
        ...(current[detail.id] || []),
        {
          quantity: "",
          brand_id: "",
          batch_number: "",
          invima_code: "",
          expiration_date: "",
          unit_price: normalizePrice(detail.unit_price),
        },
      ],
    }));
  };

  const removeLot = (detailId, rowIndex) => {
    setRows((current) => ({
      ...current,
      [detailId]: current[detailId].filter((_, index) => index !== rowIndex),
    }));
  };

  const buildDetails = () =>
    Object.entries(rows).flatMap(([purchase_order_detail_id, detailRows]) =>
      detailRows
      .filter((row) => Number(row.quantity) > 0)
      .map((row) => ({
        purchase_order_detail_id: Number(purchase_order_detail_id),
        ...row,
        quantity: Number(row.quantity),
        brand_id: Number(row.brand_id),
        unit_price: row.unit_price === "" ? null : Number(row.unit_price),
      })),
    );

  const handlePreSubmit = () => {
    const details = buildDetails();
    if (!date || details.length === 0) {
      return toast.error(
        "Seleccione la fecha y agregue al menos una cantidad recibida",
      );
    }
    if (details.some((detail) => !detail.brand_id)) {
      return toast.error("Seleccione la marca de cada producto recibido");
    }
    setShowConfirmModal(true);
  };

  const receive = async () => {
    const details = buildDetails();
    setShowConfirmModal(false);
    try {
      setSaving(true);
      await api.post(`/purchase-orders/${id}/receive`, { date, details });
      toast.success("Recepción registrada y entrada creada");
      navigate(`/purchase-orders/${id}`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "No se pudo registrar la recepción",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!order) return <p className="p-6 text-gray-500">Cargando...</p>;
  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-7xl mx-auto">
      <button
        onClick={() => navigate(`/purchase-orders/${id}`)}
        className="flex items-center gap-2 text-gray-600 mb-6"
      >
        <ArrowLeft size={20} /> Volver a la orden
      </button>
      <div className="flex flex-wrap justify-between gap-4 items-center">
        <div>
          <h2 className="text-2xl font-bold">Registrar recepción</h2>
          <p className="text-gray-500">
            {order.order_number} · {order.supplier?.name}
          </p>
        </div>
        <div>
          <label className="font-semibold">Fecha de recepción</label>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="block mt-1 border rounded px-3 py-2"
          />
        </div>
      </div>
      <div className="mt-6 space-y-6">
        {order.details.map((detail) => {
          const detailRows = rows[detail.id] || [];
          const pending =
            Number(detail.quantity) - Number(detail.received_quantity || 0);
          const totalAssigned = detailRows.reduce(
            (sum, row) => sum + Number(row.quantity || 0),
            0,
          );

          return (
            <section key={detail.id} className="border rounded-xl shadow-sm p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-3 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    {detail.product?.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Presentación: {detail.presentation?.name || "-"}
                  </p>
                </div>
                <div className="flex gap-4 text-sm">
                  <span>Solicitado: <strong>{detail.quantity}</strong></span>
                  <span>Por recibir: <strong className="text-blue-600">{pending}</strong></span>
                  <span>Distribuido: <strong className="text-green-600">{totalAssigned}</strong></span>
                </div>
              </div>

              <div className="space-y-4">
                {detailRows.map((row, rowIndex) => {
                  const quantityInOtherRows = detailRows.reduce(
                    (sum, currentRow, index) =>
                      index === rowIndex ? sum : sum + Number(currentRow.quantity || 0),
                    0,
                  );
                  const availableForRow = Math.max(0, pending - quantityInOtherRows);

                  return (
                    <div key={`${detail.id}-${rowIndex}`} className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end border rounded-lg p-3 bg-gray-50">
                      <div>
                        <label className="block text-sm font-semibold mb-1">Cantidad</label>
                        <input
                          type="number"
                          min="0"
                          max={availableForRow}
                          value={row.quantity ?? ""}
                          onChange={(event) => updateRow(detail.id, rowIndex, "quantity", event.target.value)}
                          className="w-full border rounded px-2 py-2"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold mb-1">Marca</label>
                        <Select
                          options={brands.map((brand) => ({ value: brand.id, label: brand.name }))}
                          value={brands.map((brand) => ({ value: brand.id, label: brand.name })).find((option) => option.value === Number(row.brand_id)) || null}
                          onChange={(option) => updateRow(detail.id, rowIndex, "brand_id", option?.value || "")}
                          placeholder="Seleccione una marca..."
                          isSearchable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">Lote</label>
                        <input value={row.batch_number || ""} onChange={(event) => updateRow(detail.id, rowIndex, "batch_number", event.target.value)} placeholder="NR" className="w-full border rounded px-2 py-2" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">INVIMA</label>
                        <input value={row.invima_code || ""} onChange={(event) => updateRow(detail.id, rowIndex, "invima_code", event.target.value)} placeholder="NR" className="w-full border rounded px-2 py-2" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">Vencimiento</label>
                        <input type="date" value={row.expiration_date || ""} onChange={(event) => updateRow(detail.id, rowIndex, "expiration_date", event.target.value)} className="w-full border rounded px-2 py-2" />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="min-w-0 flex-1">
                          <label className="block text-sm font-semibold mb-1">Precio</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            min="0"
                            value={formatCOPInput(row.unit_price)}
                            onChange={(event) =>
                              updateRow(
                                detail.id,
                                rowIndex,
                                "unit_price",
                                cleanNumber(event.target.value),
                              )
                            }
                            className="w-full border rounded px-2 py-2"
                          />
                        </div>
                        {detailRows.length > 1 && (
                          <button type="button" onClick={() => removeLot(detail.id, rowIndex)} className="text-red-600 hover:text-red-800 p-2 mb-0.5" title="Eliminar lote" aria-label="Eliminar lote">
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => addLot(detail)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center gap-2"
                >
                  <Plus size={16} /> Agregar lote
                </button>
              </div>
            </section>
          );
        })}
      </div>
      <div className="flex justify-between items-center mt-6">
        <p className="text-sm text-gray-500">
          Los productos con cantidad 0 no se incluirán en esta recepción.
        </p>
        <button
          onClick={handlePreSubmit}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-5 py-2 rounded flex items-center gap-2"
        >
          <Save size={18} />
          {saving ? "Registrando..." : "Confirmar recepción"}
        </button>
      </div>
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <Save className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                ¿Confirmar recepción?
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Se registrará la recepción de {buildDetails().length} productos.
              </p>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg font-semibold text-gray-700">
                Cantidad total: {buildDetails().reduce((sum, detail) => sum + detail.quantity, 0).toLocaleString("es-CO")}
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={receive}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 font-semibold"
              >
                {saving ? "Registrando..." : "Sí, confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceivePurchaseOrder;
